import type { Camera } from './footage';
import { isRecord, parseJson, text } from './json';
import { parseLocalTime } from './time';

export type ReasonKind = 'bump' | 'aeb' | 'handle' | 'manual' | 'honk' | 'object' | 'none' | 'unknown';

export interface Reason {
  readonly kind: ReasonKind;
  readonly label: string;
  readonly raw: string;
}

export interface EventInfo {
  readonly timestamp: number | undefined;
  readonly city: string;
  readonly street: string;
  readonly latitude: number | undefined;
  readonly longitude: number | undefined;
  readonly reason: Reason;
  readonly camera: Camera | undefined;
}

const BUMP_PREFIX = 'sentry_aware_accel_';
const MANUAL_PREFIX = 'user_interaction_';
const FIXED_REASONS: Readonly<Record<string, Omit<Reason, 'raw'>>> = {
  vehicle_auto_emergency_braking: { kind: 'aeb', label: 'Emergency braking' },
  sentry_locked_handle_pulled: { kind: 'handle', label: 'Handle pulled' },
  user_interaction_dashcam_launcher_action_tapped: { kind: 'manual', label: 'Saved' },
  user_interaction_honk: { kind: 'honk', label: 'Honk' },
  sentry_aware_object_detection: { kind: 'object', label: 'Motion nearby' },
  '': { kind: 'none', label: 'No reason' },
};
const CAMERA_BY_CODE: Readonly<Record<string, Camera>> = {
  0: 'front',
  1: 'front',
  2: 'front',
  3: 'left_pillar',
  4: 'right_pillar',
  5: 'left_repeater',
  6: 'right_repeater',
  7: 'back',
};

const humanize = (raw: string): string => {
  const words = raw.replace(/_/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

export function parseReason(raw: string): Reason {
  const fixed = FIXED_REASONS[raw];
  if (fixed) return { ...fixed, raw };
  if (raw.startsWith(BUMP_PREFIX)) {
    const g = Number(raw.slice(BUMP_PREFIX.length));
    return { kind: 'bump', label: Number.isFinite(g) ? `Bump ${g.toFixed(2)} g` : 'Bump', raw };
  }
  if (raw.startsWith(MANUAL_PREFIX)) return { kind: 'manual', label: humanize(raw.slice(MANUAL_PREFIX.length)), raw };
  return { kind: 'unknown', label: humanize(raw), raw };
}

export const bumpG = (reason: Reason): number =>
  reason.kind === 'bump' ? Number(reason.raw.slice(BUMP_PREFIX.length)) || 0 : 0;

function coordinate(value: unknown): number | undefined {
  const parsed = Number.parseFloat(text(value));
  return Number.isFinite(parsed) && parsed !== 0 ? parsed : undefined;
}

export function parseEventJson(json: string): EventInfo | undefined {
  const parsed = parseJson(json);
  if (!isRecord(parsed)) return undefined;
  const latitude = coordinate(parsed['est_lat']);
  const longitude = coordinate(parsed['est_lon']);
  const located = latitude !== undefined && longitude !== undefined;
  return {
    timestamp: parseLocalTime(text(parsed['timestamp'])),
    city: text(parsed['city']),
    street: text(parsed['street']),
    latitude: located ? latitude : undefined,
    longitude: located ? longitude : undefined,
    reason: parseReason(text(parsed['reason'])),
    camera: CAMERA_BY_CODE[text(parsed['camera'])],
  };
}
